import './SourceDetailPage.scss';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Dropdown } from 'primereact/dropdown';
import { Tooltip } from 'primereact/tooltip';
import { useFilters } from '../../../shared/contexts/filters/FiltersContext';
import { useAccount } from '../../../shared/contexts/account/AccountContexts';
import { useProjectDates } from '../../../shared/hooks/filters/UseProjectDates';
import { PROJECTS, SOURCES } from '../../../shared/routes/path';
import { decodeSourceHash } from '../../../shared/utils/urls';
import { chatTypeOptions, expandSelectedChatTypes, getMonitorTypesForChatType } from '../../../shared/helpers/MonitorTypeHelpers';
import { ChatType } from '../../../shared/types/monitor/ChatType';
import { getSourcesStatsForDomain } from '../../../shared/services/monitor/data/sources/MonitorDataSourcesService';
import FilterPanel from '../../../shared/components/filter-panel/FilterPanel';
import SourceInfoBox from './components/source-info-box/SourceInfoBox';
import SourceUsageChart from './components/source-usage-chart/SourceUsageChart';
import SourceUsageByModelChart from './components/source-usage-by-model-chart/SourceUsageByModelChart';
import SourcePromptsTable from './components/source-prompts-table/SourcePromptsTable';
import SourceBrandsTable from './components/source-brands-table/SourceBrandsTable';
import { useGetSourcesStatsForDomain } from '../../../shared/api/hooks/monitor/data/sources/UseGetSourcesStatsForDomain';
import { useGetBrandsForSource } from '../../../shared/api/hooks/monitor/data/sources/UseGetBrandsForSource';
import { useGetSourcesDailyStatsForDomain } from '../../../shared/api/hooks/monitor/data/sources/UseGetSourcesDailyStatsForDomain';
import { useGetPlatformStatsForSource } from '../../../shared/api/hooks/monitor/data/sources/UseGetPlatformStatsForSource';
import { usePreviousPeriodDates } from '../hooks/UsePreviousPeriodDates';
import { useSourceUsageMatrix } from './hooks/UseSourceUsageMatrix';
import { useGetCommonKeywords } from '../../../shared/api/hooks/monitor/keyword/useGetCommonKeywords';
import { isCompetitorDomain } from '../../../shared/helpers/competitor/CompetitorHelper';
import { usePromptGroups } from '../../../shared/hooks/prompt/UsePromptGroups';
import { useGetMonitorsForProject } from '../../../shared/api/hooks/monitor/useGetMonitorsForProject';

const SourceDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { projectId, sourceHash } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedChatType, selectedChatTypes, monitorPeriod, dateRange } = useFilters();
  const { startDate, endDate } = useProjectDates();
  const { accountResponse, projectResponse } = useAccount();

  const projectIdNumber = Number(projectId);
  const decodedSource = sourceHash ? decodeSourceHash(sourceHash) : '';
  const { commonKeywords } = useGetCommonKeywords(projectIdNumber);

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedGroupId = useMemo(() => {
    const param = searchParams.get('group');
    if (!param) return -1;
    const parsed = Number(param);
    return isNaN(parsed) ? -1 : parsed;
  }, [searchParams]);
  const setSelectedGroupId = useCallback((groupId: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (groupId === -1) {
        next.delete('group');
      } else {
        next.set('group', String(groupId));
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const hasPromptGroupsAddon = !!accountResponse?.addons?.includes('PROMPT_GROUP');
  const { data: promptGroups } = usePromptGroups(projectIdNumber);
  const { monitorPrompts } = useGetMonitorsForProject(projectIdNumber);

  const filteredMonitorPromptIds = useMemo(() => {
    if (!monitorPrompts || selectedGroupId === -1) return undefined;
    return monitorPrompts
      .filter((p) => p.promptGroupId === selectedGroupId)
      .map((p) => p.id)
      .filter((id): id is number => id != null);
  }, [monitorPrompts, selectedGroupId]);

  const hasExplicitModelFilter = searchParams.get('models') !== null;
  const filterMonitorTypes = useMemo(() => {
    if (!hasExplicitModelFilter || !selectedChatTypes || selectedChatTypes.length === 0) return undefined;
    const expandedChatTypes = expandSelectedChatTypes(selectedChatTypes);
    return Array.from(expandedChatTypes).flatMap(ct => getMonitorTypesForChatType(ct as ChatType));
  }, [hasExplicitModelFilter, selectedChatTypes]);

  const nonEmptyPromptGroups = useMemo(() => {
    if (!promptGroups || !monitorPrompts) return [];
    const groupIdsWithPrompts = new Set(
      monitorPrompts
        .map((p) => p.promptGroupId)
        .filter((id): id is number => id != null)
    );
    return promptGroups.filter((g) => groupIdsWithPrompts.has(g.id));
  }, [promptGroups, monitorPrompts]);

  const groupFilterOptions = useMemo(() => {
    if (!hasPromptGroupsAddon || nonEmptyPromptGroups.length === 0) return [];
    return [
      { label: t('allGroups'), value: -1 },
      ...nonEmptyPromptGroups.map((g) => ({ label: g.name, value: g.id })),
    ];
  }, [hasPromptGroupsAddon, nonEmptyPromptGroups, t]);

  const currentProject = useMemo(
    () => projectResponse?.find((p) => p.id === projectIdNumber),
    [projectResponse, projectIdNumber]
  );

  // Extract domain, path, and clean URL from source
  let sourceDomain = '';
  let cleanUrl = decodedSource;
  let siteName = '';
  try {
    const url = new URL(decodedSource.startsWith('http') ? decodedSource : `https://${decodedSource}`);
    sourceDomain = url.hostname;
    cleanUrl = `${url.origin}${url.pathname}`;
    const hostWithoutWww = sourceDomain.replace(/^www\./, '');
    siteName = hostWithoutWww.split('.')[0];
  } catch {
    sourceDomain = decodedSource;
    siteName = decodedSource;
  }

  // Fetch source stats for current period (keyword = source URL to find exact match)
  const { domainSourcesStats: currentStats, isLoading: isCurrentLoading } = useGetSourcesStatsForDomain(
    projectIdNumber,
    sourceDomain,
    selectedChatType,
    startDate,
    endDate,
    1,
    100,
    decodedSource,
    undefined,
    undefined,
    undefined,
    filteredMonitorPromptIds,
    filterMonitorTypes
  );

  // Fetch source stats for previous period
  const previousPeriod = usePreviousPeriodDates(startDate, endDate);

  const { domainSourcesStats: previousStats } = useGetSourcesStatsForDomain(
    projectIdNumber,
    sourceDomain,
    selectedChatType,
    previousPeriod?.previousStartDate ?? null,
    previousPeriod?.previousEndDate ?? null,
    1,
    100,
    decodedSource,
    undefined,
    undefined,
    undefined,
    filteredMonitorPromptIds,
    filterMonitorTypes
  );

  // Fetch brands for this source
  const { brandsForSource, isLoading: isBrandsLoading } = useGetBrandsForSource(
    projectIdNumber,
    decodedSource,
    selectedChatType,
    startDate,
    endDate,
    filteredMonitorPromptIds,
    filterMonitorTypes
  );

  // Find the exact source in results
  const currentSource = useMemo(
    () => currentStats?.sources?.find((s) => s.source === decodedSource),
    [currentStats, decodedSource]
  );

  const showComparison = currentSource?.firstSeen?.slice(0, 10) !== currentSource?.lastSeen?.slice(0, 10);

  // Period state for usage chart aggregation (DAILY / WEEKLY / MONTHLY)
  const [usagePeriod, setUsagePeriod] = useState<'DAILY' | 'WEEKLY' | 'MONTHLY'>('DAILY');

  // Fetch daily stats for usage chart
  const { sourcesDailyStats: currentDailyStats, isLoading: isDailyStatsLoading } = useGetSourcesDailyStatsForDomain(
    projectIdNumber,
    sourceDomain,
    selectedChatType,
    usagePeriod,
    startDate,
    endDate,
    filteredMonitorPromptIds,
    filterMonitorTypes
  );

  const { sourcesDailyStats: previousDailyStats } = useGetSourcesDailyStatsForDomain(
    projectIdNumber,
    sourceDomain,
    selectedChatType,
    usagePeriod,
    previousPeriod?.previousStartDate ?? null,
    previousPeriod?.previousEndDate ?? null,
    filteredMonitorPromptIds,
    filterMonitorTypes
  );

  const { matrix: usageMatrix, headers: usageHeaders } = useSourceUsageMatrix(currentDailyStats, previousDailyStats, decodedSource, showComparison);

  // Fetch platform stats for usage by model chart
  const { platformStats: currentPlatformStats, isLoading: isPlatformStatsLoading } = useGetPlatformStatsForSource(
    projectIdNumber,
    decodedSource,
    selectedChatType,
    startDate,
    endDate,
    filteredMonitorPromptIds,
    filterMonitorTypes
  );

  const { platformStats: previousPlatformStats } = useGetPlatformStatsForSource(
    projectIdNumber,
    decodedSource,
    selectedChatType,
    previousPeriod?.previousStartDate ?? null,
    previousPeriod?.previousEndDate ?? null,
    filteredMonitorPromptIds,
    filterMonitorTypes
  );

  const previousSource = useMemo(
    () => previousStats?.sources?.find((s) => s.source === decodedSource),
    [previousStats, decodedSource]
  );

  const usageChange = useMemo(() => {
    if (currentSource === undefined || previousSource === undefined) return undefined;
    return currentSource.count - previousSource.count;
  }, [currentSource, previousSource]);

  const promptCountChange = useMemo(() => {
    if (currentSource === undefined || previousSource === undefined) return undefined;
    return currentSource.promptCount - previousSource.promptCount;
  }, [currentSource, previousSource]);

  const domainCategory = useMemo(() => {
    if (!currentProject) return null;

    // Manual classification override takes highest priority
    const classification = currentProject.domainClassification?.[sourceDomain];
    if (classification) return classification;

    const competition = currentProject.competition ?? [];
    const firmName = currentProject.firmName?.toLowerCase().trim() ?? '';
    const competitorKeywords = (commonKeywords ?? []).filter(
      (k) => k.normalizedKeyword.toLowerCase() !== firmName
    );

    if (isCompetitorDomain(sourceDomain, competitorKeywords, competition)) return 'COMPETITOR';

    const projectWebsite = currentProject.website || '';
    if (projectWebsite && sourceDomain.includes(projectWebsite.replace(/^(https?:\/\/)?(www\.)?/, '').replace(/\/.*$/, ''))) {
      return 'OWN';
    }

    return null;
  }, [currentProject, sourceDomain, commonKeywords]);

  const isLockedModel = useMemo(() => {
    if (!accountResponse || !selectedChatType || selectedChatType === 'ALL') return false;
    const available = chatTypeOptions(accountResponse.features);
    return !available.some((opt) => opt.type === selectedChatType);
  }, [accountResponse, selectedChatType]);

  const sourceExistsQueryEnabled = !!projectIdNumber && !!sourceDomain && !!decodedSource && !!dateRange;
  const { data: sourceExistsData, isLoading: isSourceExistsLoading } = useQuery({
    queryKey: ['source-exists-check', projectIdNumber, decodedSource],
    queryFn: () =>
      getSourcesStatsForDomain(
        projectIdNumber,
        sourceDomain,
        'ALL',
        dateRange!.oldestDate,
        dateRange!.newestDate,
        1,
        1,
        decodedSource
      ),
    staleTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: sourceExistsQueryEnabled,
  });

  const sourceNotFound =
    sourceExistsQueryEnabled && !isSourceExistsLoading && (sourceExistsData?.sources?.length === 0 || !decodedSource);

  const sourcesUrl = `${SOURCES}/${projectId}/${monitorPeriod?.toLowerCase() || 'daily'}/${selectedChatType?.toLowerCase() || 'all'}${location.search}`;

  useEffect(() => {
    if (!accountResponse || !projectId) return;
    if (!accountResponse.addons.includes('SOURCES_3_0')) {
      navigate(PROJECTS, { replace: true });
    } else if (isLockedModel || sourceNotFound) {
      navigate(sourcesUrl, { replace: true });
    }
  }, [accountResponse, projectId, isLockedModel, sourceNotFound, navigate, sourcesUrl]);

  if (!accountResponse || !accountResponse.addons.includes('SOURCES_3_0') || !projectId || isLockedModel || sourceNotFound) {
    return <></>;
  }

  const sourceHref = decodedSource.startsWith('http') ? decodedSource : `https://${decodedSource}`;

  return (
    <div className="source-detail-page page-content">
      <div className="page-box">
        <div className="page-box__filters" data-observe-sticky>
          <div className="title-box">
            <div className="title-box__title">
              <div className="title-box__nav">
                <nav className="breadcrumbs">
                  <ul>
                    <li>
                      <Link to={sourcesUrl}>{t('sources')}</Link>
                    </li>
                    <li>
                      <Link to={`${SOURCES}/${projectId}/domain/${encodeURIComponent(sourceDomain)}/${monitorPeriod?.toLowerCase() || 'daily'}/${selectedChatType?.toLowerCase() || 'all'}${location.search}`}>
                        {sourceDomain}
                      </Link>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
            <div className="title-box__action">
              <div className="filters">
                <FilterPanel baseUrl={location.pathname} showPeriodTabs={false}>
                  {groupFilterOptions.length > 0 && (
                    <Dropdown
                      value={selectedGroupId}
                      onChange={(e) => setSelectedGroupId(e.value)}
                      options={groupFilterOptions}
                      optionLabel="label"
                      optionValue="value"
                      className="dropdown dropdown--group-filter"
                      valueTemplate={(option) => (
                        <>
                          <span id={`group-filter-val-source-${option?.value}`} className="dropdown--group-filter__label">
                            {option?.label}
                          </span>
                          {option?.label && (
                            <Tooltip target={`#group-filter-val-source-${option.value}`} content={option.label} position="top" showDelay={300} />
                          )}
                        </>
                      )}
                      itemTemplate={(option) => (
                        <>
                          <span id={`group-filter-item-source-${option.value}`} className="dropdown--group-filter__label">
                            {option.label}
                          </span>
                          <Tooltip target={`#group-filter-item-source-${option.value}`} content={option.label} position="top" showDelay={300} />
                        </>
                      )}
                    />
                  )}
                </FilterPanel>
              </div>
            </div>
          </div>
        </div>

        <div className="page-box__stats">
          <div className="stats stats--full-width">
            <SourceInfoBox
              siteName={siteName}
              sourceDomain={sourceDomain}
              cleanUrl={cleanUrl}
              sourceHref={sourceHref}
              domainCategory={domainCategory}
              urlType={currentSource?.pageType}
              usageCount={currentSource?.count}
              usageChange={usageChange}
              promptCount={currentSource?.promptCount}
              promptCountChange={promptCountChange}
              firstSeen={currentSource?.firstSeen}
              lastSeen={currentSource?.lastSeen}
            />
          </div>
          <div className="stats">
            <SourceUsageChart
              matrix={usageMatrix}
              headers={usageHeaders}
              isLoading={isDailyStatsLoading}
              activePeriod={usagePeriod}
              onPeriodChange={setUsagePeriod}
            />
            <SourceUsageByModelChart
              currentPlatformStats={currentPlatformStats}
              previousPlatformStats={previousPlatformStats}
              isLoading={isPlatformStatsLoading}
              features={accountResponse.features || []}
              chatType={selectedChatType}
            />
          </div>
          <div className="stats stats--full-width">
            <SourcePromptsTable
              prompts={currentSource?.prompts ?? []}
              isLoading={isCurrentLoading}
              projectId={projectId}
            />
          </div>
          {(isBrandsLoading || (brandsForSource?.brands?.length ?? 0) > 0) && (
            <div className="stats stats--full-width">
              <SourceBrandsTable
                brands={brandsForSource?.brands ?? []}
                isLoading={isBrandsLoading}
                siteName={siteName}
                commonKeywords={commonKeywords}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SourceDetailPage;
